import {
  type Role,
  type DisconnectionDetails as DisconnectionDetailsBase,
  useConversation,
  type HookOptions,
} from "@elevenlabs/react";
import { useMemo, useRef, useState } from "react";
import { useSpinDelay } from "spin-delay";
import { getErrorMessageFromUnknownError, handleMicrophoneError } from "@utilities/errors";
import { logger } from "@utilities/logger";
import { toast } from "@/app/_providers/toast";
import { useTranslations } from "next-intl";
import PQueue from "p-queue";
import { flushSync } from "react-dom";
import { EventName } from "@mw/shared/analyticsEventNames";
import { captureEvent } from "@/app/_analytics/analytics";
import { useRouter } from "@/i18n/navigation";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { getTemplatePreviewSignedUrl } from "@/app/_views/AgentTemplates/templateView/getTemplatePreviewSignedUrl";
import { stripAudioTags } from "@ui/components/core/Orb/utils";

export type DisconnectionDetails = DisconnectionDetailsBase & {
  error?: unknown;
};

interface Message {
  role: Role;
  message: string;
}

export interface MessageWithId extends Message {
  id: number;
}

export interface SessionMessage extends MessageWithId {
  isInitial: boolean;
  mode: "text" | "voice";
}

export type AgentMode = "speaking" | "listening" | "thinking";

export type AgentSession = ReturnType<typeof useAgentSession>;

interface UseAgentSessionOptionsBase {
  voiceId?: string | null;
  defaultMode?: "text" | "voice";
  onDisconnect?: (details: DisconnectionDetails) => void;
  onBeforeSetMessages?: () => void;
  clearMessagesOnDisconnect?: boolean;
  showErrors?: boolean;
  captureEvents?: boolean;
  onSessionStarted?: (params: {
    agentId?: string;
    templateId?: string;
    conversationId: string;
    conversationType: "text" | "voice";
    language: string;
  }) => void;
  onSessionFailed?: (params: {
    agentId?: string;
    templateId?: string;
    conversationType: "text" | "voice";
    errorMessage: string;
  }) => void;
  onSessionEnded?: (params: { agentId?: string; templateId?: string; durationSecs: number | null }) => void;
  onAddMessage?: (message: Message & { mode: "text" | "voice" }) => void;
  /** MS before the thinking `agentMode` is shown. */
  thinkingDelay?: number;
  /** MS the thinking `agentMode` stays visible at minimum once shown. */
  thinkingMinDuration?: number;
}

/** Standard agent conversation — requires `agentId`. */
interface UseAgentSessionAgentOptions extends UseAgentSessionOptionsBase {
  agentId: string;
  templateId?: never;
}

/** Anonymous template preview conversation — requires `templateId`, uses hCaptcha + signedUrl. */
interface UseAgentSessionTemplateOptions extends UseAgentSessionOptionsBase {
  agentId?: never;
  templateId: string;
}

export type UseAgentSessionOptions = UseAgentSessionAgentOptions | UseAgentSessionTemplateOptions;

export type AgentSessionStartOptions = {
  language?: string;
  dynamicVariables?: Record<string, string | number | boolean>;
  firstMessage?: string;
};

export function useAgentSession({
  agentId,
  templateId,
  voiceId,
  defaultMode = "voice",
  onDisconnect: onDisconnectCallback,
  onBeforeSetMessages,
  clearMessagesOnDisconnect = true,
  showErrors = true,
  captureEvents = true,
  onSessionStarted,
  onSessionFailed,
  onSessionEnded,
  onAddMessage,
  thinkingDelay = 1000,
  thinkingMinDuration = 500,
}: UseAgentSessionOptions) {
  const tMicrophoneErrors = useTranslations("ClientKeys.Errors.Microphone");
  const router = useRouter();
  const [messages, setMessages] = useState<MessageWithId[]>([]);
  const [sessions, setSessions] = useState<
    { id: number; messages: SessionMessage[]; startTime: Date; endTime?: Date }[]
  >([]);
  const [mode, setMode] = useState<"text" | "voice">(defaultMode);
  const textInputRef = useRef<HTMLInputElement>(null);
  const callStartTimeRef = useRef<number | null>(null);
  const isTextOnlyRef = useRef(false);

  // Wait for the first `onModeChange` before returning `agentMode`
  const [isSpeakingIsValid, setIsSpeakingIsValid] = useState(false);
  const [isWaitingForAgent, setIsWaitingForAgent] = useState(false);
  const showThinking = useSpinDelay(isWaitingForAgent, {
    delay: thinkingDelay,
    minDuration: thinkingMinDuration,
    ssr: false,
  });

  const defaultModeRef = useRef(defaultMode);
  defaultModeRef.current = defaultMode;
  const onDisconnectRef = useRef(onDisconnectCallback);
  onDisconnectRef.current = onDisconnectCallback;
  const onBeforeSetMessagesRef = useRef(onBeforeSetMessages);
  onBeforeSetMessagesRef.current = onBeforeSetMessages;

  const messageQueueRef = useRef<PQueue | null>(null);
  if (!messageQueueRef.current) {
    messageQueueRef.current = new PQueue({ interval: 500, intervalCap: 1 });
  }

  function resetMessages() {
    setMessages([]);
    setSessions([]);
  }

  function resetOnDisconnect(details: DisconnectionDetails) {
    messageQueueRef.current?.clear();
    if (clearMessagesOnDisconnect) {
      setMessages([]);
      setSessions([]);
    }
    setIsWaitingForAgent(false);
    setMode(defaultModeRef.current);
    onDisconnectRef.current?.(details);
  }

  const conversation = useConversation({
    onMessage: addMessage,
    onDisconnect: (details: DisconnectionDetails) => {
      setIsPendingDisconnect(false);
      resetOnDisconnect(details);
      setIsSpeakingIsValid(false);
    },
    onModeChange: () => {
      setIsSpeakingIsValid(true);
    },
    clientTools: {
      redirectToPage: ({ path }: { path: string }) => {
        router.push(path);
      },
    },
  });

  const [isPendingDisconnect, setIsPendingDisconnect] = useState(false);
  const status = isPendingDisconnect ? "disconnecting" : conversation.status;
  const sessionIsActive = status === "connecting" || status === "connected";
  const voiceIsActive = sessionIsActive && mode === "voice";
  const agentMode = useMemo(() => {
    if (!isSpeakingIsValid) {
      return null;
    }
    if (showThinking) {
      return "thinking";
    }
    if (conversation.isSpeaking) {
      return "speaking";
    }
    return "listening";
  }, [conversation.isSpeaking, isSpeakingIsValid, showThinking]);

  function addMessage(incoming: Message) {
    if (incoming.role === "user") {
      setIsWaitingForAgent(true);
    }
    const lineMode = isTextOnlyRef.current ? "text" : "voice";
    const cleanedMessage = incoming.role === "agent" ? stripAudioTags(incoming.message) : incoming.message;
    messageQueueRef.current?.add(() => {
      onBeforeSetMessagesRef.current?.();
      if (incoming.role === "agent") {
        setIsWaitingForAgent(false);
      }
      setMessages(prev => [...prev, { id: prev.length, role: incoming.role, message: cleanedMessage }]);
      setSessions(prevSess => {
        const last = prevSess[prevSess.length - 1];
        const initialMessageIndex = last.messages.findIndex(m => m.role === "agent");
        const isInitialMessage = incoming.role === "agent" && initialMessageIndex === -1;
        return [
          ...prevSess.slice(0, -1),
          {
            ...last,
            messages: [
              ...last.messages,
              {
                id: last.messages.length,
                role: incoming.role,
                message: cleanedMessage,
                isInitial: isInitialMessage,
                mode: lineMode,
              },
            ],
          },
        ];
      });
    });
    if (captureEvents) {
      captureEvent(EventName.mw_hero_agent_message_sent, {
        sender_role: incoming.role,
        message_type: lineMode,
      });
    }
    onAddMessage?.({ ...incoming, message: cleanedMessage, mode: lineMode });
  }

  const isTemplateSession = !agentId && !!templateId;

  async function startSession({
    textOnly = false,
    language,
    dynamicVariables,
    firstMessage,
  }: AgentSessionStartOptions & { textOnly?: boolean } = {}): Promise<boolean> {
    try {
      isTextOnlyRef.current = textOnly;
      const overrides: HookOptions["overrides"] = {};
      if (voiceId) {
        overrides.tts = { voiceId };
      }
      if (language || firstMessage) {
        overrides.agent = {
          ...(language ? { language } : {}),
          ...(firstMessage ? { firstMessage } : {}),
        };
      }
      setSessions(prev => [...prev, { id: prev.length, messages: [], startTime: new Date() }]);

      let conversationId: string;

      if (isTemplateSession) {
        // Template preview path: obtain hCaptcha token and use signedUrl
        const hcaptchaToken = await getCaptchaToken({ behaviourType: "agent_template_preview" });
        const signedUrl = getTemplatePreviewSignedUrl({ templateId: templateId!, hcaptchaToken });
        conversationId = await conversation.startSession({
          signedUrl,
          overrides,
          dynamicVariables,
          connectionType: "websocket",
          textOnly,
        });
      } else {
        // Standard agent path
        conversationId = await conversation.startSession({
          agentId: agentId!,
          overrides,
          dynamicVariables,
          connectionType: "websocket",
          textOnly,
        });
      }

      callStartTimeRef.current = Date.now();
      if (captureEvents) {
        captureEvent(EventName.mw_hero_agent_conversation_started, {
          ...(isTemplateSession ? { template_id: templateId } : { agent_id: agentId }),
          conversation_type: textOnly ? "text" : "voice",
        });
      }
      onSessionStarted?.({
        ...(isTemplateSession ? { templateId } : { agentId }),
        conversationId,
        conversationType: textOnly ? "text" : "voice",
        language: language ?? "en",
      });
      return true;
    } catch (error) {
      logger.error(error instanceof Error ? error : null, "Failed to start session");
      if (error instanceof Error) {
        handleMicrophoneError(error, {
          handler: (title, description) => {
            if (showErrors) {
              toast.error(title, { description });
            }
          },
          t: tMicrophoneErrors,
          demoType: "conv_ai_orb",
        });
      }

      resetOnDisconnect({
        reason: "error",
        message: getErrorMessageFromUnknownError(error),
        context: new Event("error"),
        error,
      });

      if (captureEvents) {
        captureEvent(EventName.mw_hero_agent_conversation_failed, {
          ...(isTemplateSession ? { template_id: templateId } : { agent_id: agentId }),
          conversation_type: textOnly ? "text" : "voice",
          error_message: getErrorMessageFromUnknownError(error),
        });
      }
      onSessionFailed?.({
        ...(isTemplateSession ? { templateId } : { agentId }),
        conversationType: textOnly ? "text" : "voice",
        errorMessage: getErrorMessageFromUnknownError(error),
      });
      return false;
    }
  }

  function endSession() {
    if (status === "connecting") {
      setIsPendingDisconnect(true);
      resetOnDisconnect({ reason: "user" });
    }
    conversation.endSession();
    setSessions(prev => [...prev.slice(0, -1), { ...prev[prev.length - 1], endTime: new Date() }]);

    const durationSecs = callStartTimeRef.current ? Math.floor((Date.now() - callStartTimeRef.current) / 1000) : null;
    callStartTimeRef.current = null;
    if (captureEvents) {
      captureEvent(EventName.mw_hero_agent_conversation_ended, {
        ...(isTemplateSession ? { template_id: templateId } : { agent_id: agentId }),
        call_duration_secs: durationSecs,
      });
    }
    onSessionEnded?.({ ...(isTemplateSession ? { templateId } : { agentId }), durationSecs });
  }

  function startVoiceSession(options: AgentSessionStartOptions = {}) {
    if (sessionIsActive) return;
    startSession({ ...options, textOnly: false });
  }

  function startTextSession(options: AgentSessionStartOptions = {}) {
    if (sessionIsActive) return;
    startSession({ ...options, textOnly: true });
    flushSync(() => {
      setMode("text");
    });
    textInputRef.current?.focus();
  }

  function sendTextMessage(text: string) {
    if (!text.trim() || !sessionIsActive) return;
    addMessage({ role: "user", message: text });
    conversation.sendUserMessage(text);
  }

  async function startTextSessionWithMessage(text: string, { language }: { language?: string } = {}) {
    const trimmed = text.trim();
    if (!trimmed || sessionIsActive) return;
    const success = await startSession({ textOnly: true, language });
    flushSync(() => {
      setMode("text");
    });
    textInputRef.current?.focus();
    if (success) {
      addMessage({ role: "user", message: trimmed });
      conversation.sendUserMessage(trimmed);
    }
  }

  async function startVoiceSessionWithMessage(text: string, { language }: { language?: string } = {}) {
    const trimmed = text.trim();
    if (!trimmed || sessionIsActive) return;
    const success = await startSession({ textOnly: false, language });
    flushSync(() => {
      setMode("voice");
    });
    if (success) {
      addMessage({ role: "user", message: trimmed });
      conversation.sendUserMessage(trimmed);
    }
  }

  return {
    conversation,
    sessions,
    messages,
    status,
    sessionIsActive,
    voiceIsActive,
    isPendingDisconnect,
    mode,
    setMode,
    textInputRef,
    startVoiceSession,
    startTextSession,
    startTextSessionWithMessage,
    startVoiceSessionWithMessage,
    endSession,
    addMessage,
    sendTextMessage,
    resetMessages,
    agentMode,
  };
}
